import {
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import { WarningOutlined } from "@ant-design/icons";
import { AlertTriangle, Download, Search, TrendingUp, X } from "lucide-react";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import {
  dashboardDataTable as dt,
  DashboardSectionHeader,
  DashboardNarrativePanel,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DashboardKpiCard,
  TablePagePagination,
  toggleTableSort,
  DefinitionTooltip,
  type DefinitionTooltipInfo,
  type SortDirection,
} from "src/components/common";
import { useDashboardNarrative } from "src/hooks/useDashboardNarrative";
import MonthRangePicker from "src/components/reusable/MonthRangePicker/MonthRangePicker";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  formatNumber,
  formatNumberDigits,
  toArabicIndicDigitsInString,
  wrapBidiIsolate,
} from "src/utils/numberFormat";
import { getDashboardKpiLucideIconComponent } from "src/utils/dashboardKpiLucideIcon";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { useCLTVDashboard } from "../../hooks/useCLTVDashboard";
import {
  fetchCLTVCustomerAnalytics,
  type CLTVKPICard,
  type CustomerAnalyticsRow,
  type PredictiveInsightItem,
  type RiskTier,
} from "src/services/CLTVDashboardAPI";
import {
  CLTV_SEGMENT_COLORS,
  defaultCltvSegmentColor,
} from "./CLTVDashboard.data";
import {
  cltvIdSlug,
  cltvRiskTierCode,
  cltvSegmentNameSlug,
  getCltvColumnDefinition,
  getCltvInsightDefinition,
  getCltvKpiDefinition,
  getCltvSectionDefinition,
  type CltvRiskTierCode,
} from "./cltvDefinitions";
import { CHART_COLOR_PALETTE } from "src/components/charts/chartPalette";
import { DASHBOARD_CHANGE_FALLBACK_MAX } from "src/utils/dashboardChangeFallback";
import { calculateBarWidthPct } from "src/utils/chartBarScale";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import "./CLTVDashboard.scss";

type EvolutionSeriesKey =
  | "loyal"
  | "highSpender"
  | "likelyToChurn"
  | "idle"
  | "oneTimer"
  | "indecisive"
  | "cartAbandoners"
  | "unsubscribed";

type InsightDisplay = {
  key: string;
  title: string;
  subtitle: string;
  value: string | number;
  badge: string;
  progress: number;
  tone: "positive" | "warning" | "info";
  raw: PredictiveInsightItem;
};

/** Swaps Western digits for Eastern Arabic-Indic ones, keeping the surrounding punctuation. */
const toLocaleDigits = (value: string, isArabic: boolean) =>
  isArabic ? wrapBidiIsolate(toArabicIndicDigitsInString(value)) : value;

/**
 * KPI label with the curated translation first and the API's own string as the fallback.
 *
 * `t(key, { defaultValue: apiValue })` cannot be used directly: when `apiValue` is `undefined`
 * i18next has no default to return and hands back the *key itself*, which is truthy — so a card
 * whose id has no `cltv.kpi.*` entry rendered `cltv.kpi.<id>.badge` as its badge text. Passing an
 * empty `defaultValue` keeps a missing key falsy, so the API value (or nothing) is used instead.
 */
const resolveKpiLabel = (t: TFunction, key: string, apiValue: string | undefined): string =>
  t(key, { defaultValue: "" }) || apiValue?.trim() || "";

const RISK_TIER_ORDER: Record<CltvRiskTierCode, number> = { high: 0, medium: 1, low: 2 };

const RISK_TIER_COLORS: Record<CltvRiskTierCode, string> = {
  high: "#e05252",
  medium: "#f6a623",
  low: "#4caf50",
};

const RISK_TIER_FALLBACK_COLOR = "#8d4fd3";

const insightToneColor = (tone: InsightDisplay["tone"]) => {
  if (tone === "positive") {
    return "#94d0b5";
  }
  if (tone === "warning") {
    return "#f6c15f";
  }
  return "#8d4fd3";
};

type CustomerSegmentTone =
  | "loyal"
  | "high-spender"
  | "likely-to-churn"
  | "one-timer"
  | "idle"
  | "indecisive"
  | "cart-abandoner"
  | "unsubscribed";

// Only the fields the backend accepts for `sort_by`. Segments and predicted
// CLTV are not sortable server-side (they'd return 422).
type CustomerTableSortKey =
  | "customerName"
  | "email"
  | "profile"
  | "totalSpend"
  | "totalOrders"
  | "predictedSpend"
  | "predictedOrders";

const SORT_KEY_TO_API_FIELD: Record<CustomerTableSortKey, string> = {
  customerName: "customer_name",
  email: "email",
  profile: "customer_age_months",
  totalSpend: "total_spend",
  totalOrders: "total_orders",
  predictedSpend: "predicted_spend_3m",
  predictedOrders: "predicted_orders_3m",
};

const SEGMENT_PILL_FILTERS: Array<{ key: string; labelKey: string }> = [
  { key: "loyal", labelKey: "cltv.segmentNames.loyal" },
  { key: "high_spender", labelKey: "cltv.segmentNames.high_spender" },
  { key: "likely_to_churn", labelKey: "cltv.segmentNames.likely_to_churn" },
  { key: "idle", labelKey: "cltv.segmentNames.idle" },
  { key: "one_timer", labelKey: "cltv.segmentNames.one_timer" },
  { key: "cart_abandoner", labelKey: "cltv.segmentNames.cart_abandoner" },
  { key: "indecisive", labelKey: "cltv.segmentNames.indecisive" },
  { key: "unsubscribed", labelKey: "cltv.segmentNames.unsubscribed" },
];

const CltvSectionTitle: FC<{
  title: string;
  tooltip?: DefinitionTooltipInfo;
}> = ({ title, tooltip }) => {
  const { t } = useTranslation();
  return (
    <div className="cltv-dashboard__title-with-tooltip">
      <h3>{title}</h3>
      {tooltip ? (
        <DefinitionTooltip
          label={title}
          info={tooltip}
          formulaTitle={t("cltv.common.formula")}
        />
      ) : null}
    </div>
  );
};

const SpendingTooltip: FC<{
  active?: boolean;
  label?: string;
  payload?: Array<{
    name?: string;
    dataKey?: string;
    value: number;
    payload?: {
      fullLabel?: string;
      status?: string;
      predictedRaw?: number | null;
      actualRaw?: number | null;
      predictedFormattedValue?: string;
      actualFormattedValue?: string;
    };
  }>;
  formatBucket?: (_raw: string) => string;
  formatMoney: (_value: number) => string;
}> = ({ active, label, payload, formatBucket, formatMoney }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const labelText =
    label !== undefined && label !== null && formatBucket
      ? formatBucket(String(label))
      : label;

  const point = payload[0]?.payload;
  const status = point?.status ?? "forecast";
  const statusLabel = t(`cltv.spendingStatus.${status}`, {
    defaultValue: t("cltv.spendingStatus.forecast"),
  });

  const predictedCurrency = extractCurrencyCode(point?.predictedFormattedValue);
  const reconcileCurrency = (formattedValue: string | undefined) => {
    if (!formattedValue || !predictedCurrency) {
      return formattedValue;
    }
    const rowCurrency = extractCurrencyCode(formattedValue);
    if (!rowCurrency || rowCurrency === predictedCurrency) {
      return formattedValue;
    }
    return formattedValue.replace(rowCurrency, predictedCurrency);
  };
  const rows = [
    {
      key: "predicted",
      label: t("cltv.spendingTooltip.predicted"),
      value: point?.predictedRaw ?? null,
      formattedValue: point?.predictedFormattedValue,
    },
    {
      key: "actual",
      label: t("cltv.spendingTooltip.actual"),
      value: point?.actualRaw ?? null,
      formattedValue: reconcileCurrency(point?.actualFormattedValue),
    },
  ].filter(row => row.value !== null && row.value !== undefined);

  return (
    <div className="analytics-chart-tooltip">
      <div className="analytics-chart-tooltip__bar" />
      <div className="analytics-chart-tooltip__content">
        <div className="analytics-chart-tooltip__header">
          <strong>{point?.fullLabel ?? labelText}</strong>
          <span className="analytics-chart-tooltip__pill">{statusLabel}</span>
        </div>
        {rows.map(row => (
          <span key={row.key} className="analytics-chart-tooltip__value">
            <span className="analytics-chart-tooltip__name">
              {row.label}
            </span>{" "}
            {row.formattedValue ? normalizeCurrencyLabel(row.formattedValue) : formatMoney(row.value ?? 0)}
          </span>
        ))}
      </div>
    </div>
  );
};

const EvolutionTooltip: FC<{
  active?: boolean;
  label?: string;
  payload?: Array<{ name: string; value: number; color?: string }>;
  formatDate: (_value: string) => string;
}> = ({
  active,
  label,
  payload,
  formatDate,
}) => {
    const { t, i18n } = useTranslation();
    if (!active || !payload?.length) {
      return null;
    }

    const seriesLabel = (name: string) =>
      t(`cltv.segments.${name}`, {
        defaultValue: name.replace(/([a-z])([A-Z])/g, "$1 $2"),
      });

    const sortedPayload = [...payload]
      .filter(item => Number(item.value) > 0)
      .sort((a, b) => Number(b.value) - Number(a.value));

    return (
      <div className="analytics-chart-tooltip">
        <div className="analytics-chart-tooltip__bar" />
        <div className="analytics-chart-tooltip__content">
          <span className="analytics-chart-tooltip__label">
            {formatDate(String(label ?? ""))}
          </span>
          {sortedPayload.map(item => (
            <span key={item.name} className="analytics-chart-tooltip__value">
              <span className="analytics-chart-tooltip__row-label">
                <i style={{ backgroundColor: item.color ?? "#98a2b3" }} />
                <span className="analytics-chart-tooltip__name">{seriesLabel(item.name)}:</span>
              </span>{" "}
              {formatNumber(item.value, {}, i18n.language)}
            </span>
          ))}
        </div>
      </div>
    );
  };

const FLOATING_TOOLTIP_MARGIN = 8;
const FLOATING_TOOLTIP_GAP = 12;

const clampValue = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(max, min));

/** Viewport pixel of a hovered Recharts point — anchor to the wrapper, not the outer chart div. */
const getChartHoverViewportPoint = (
  container: HTMLDivElement,
  activeCoordinate: { x: number; y: number },
) => {
  const wrapper = container.querySelector<HTMLElement>(".recharts-wrapper");
  const anchorRect = wrapper?.getBoundingClientRect() ?? container.getBoundingClientRect();
  return {
    x: anchorRect.left + activeCoordinate.x,
    y: anchorRect.top + activeCoordinate.y,
  };
};

/**
 * Positions one axis beside `point` — after it (right / below) by default, flipped
 * before it when that overflows, else whichever side has more room — then clamps
 * the result into `[minEdge, maxEdge]`.
 *
 * `preferBefore` forces the "before" (left/up) side as the starting preference instead
 * of "after". Used for the horizontal axis in Arabic: RTL charts read right-to-left, so
 * a point near the chart's start (visually left, since the chart surface itself stays
 * LTR — see chartRtl.ts) tends to sit close to the viewport's own left edge. Preferring
 * "after" there still fits per the math, but combined with the box's own reported width
 * sometimes under-measuring (see the comment in FloatingTooltip below), the margin ends
 * up too thin and the box's left edge visually meets the viewport edge. Preferring
 * "before" for RTL points the box away from that edge by default instead.
 */
const placeAlongAxis = (
  point: number,
  boxSize: number,
  minEdge: number,
  maxEdge: number,
  preferBefore = false,
  gap = FLOATING_TOOLTIP_GAP
) => {
  const afterStart = point + gap;
  const beforeStart = point - boxSize - gap;
  const fitsAfter = afterStart + boxSize <= maxEdge - FLOATING_TOOLTIP_MARGIN;
  const fitsBefore = beforeStart >= minEdge + FLOATING_TOOLTIP_MARGIN;

  let start = preferBefore ? beforeStart : afterStart;
  const preferredFits = preferBefore ? fitsBefore : fitsAfter;
  if (!preferredFits) {
    const fallbackStart = preferBefore ? afterStart : beforeStart;
    const fallbackFits = preferBefore ? fitsAfter : fitsBefore;
    if (fallbackFits) {
      start = fallbackStart;
    } else {
      start = point - minEdge > maxEdge - point ? beforeStart : afterStart;
    }
  }

  return clampValue(
    start,
    minEdge + FLOATING_TOOLTIP_MARGIN,
    maxEdge - boxSize - FLOATING_TOOLTIP_MARGIN
  );
};

/** Arabic spending card: anchor right of the point, or below if horizontal room is tight. */
const placeRtlRightOrBelow = (
  pointX: number,
  pointY: number,
  boxWidth: number,
  boxHeight: number,
  horizontalMin: number,
  horizontalMax: number,
  verticalMin: number,
  verticalMax: number,
) => {
  const hMin = horizontalMin + FLOATING_TOOLTIP_MARGIN;
  const hMax = horizontalMax - boxWidth - FLOATING_TOOLTIP_MARGIN;
  const vMin = verticalMin + FLOATING_TOOLTIP_MARGIN;
  const vMax = verticalMax - boxHeight - FLOATING_TOOLTIP_MARGIN;

  const rightStart = pointX + FLOATING_TOOLTIP_GAP;
  const fitsRight =
    hMax >= hMin &&
    rightStart >= hMin &&
    rightStart + boxWidth <= horizontalMax - FLOATING_TOOLTIP_MARGIN;

  if (fitsRight) {
    const belowStart = pointY + FLOATING_TOOLTIP_GAP;
    const aboveStart = pointY - boxHeight - FLOATING_TOOLTIP_GAP;
    let top = belowStart;
    if (belowStart > vMax && aboveStart >= vMin) {
      top = aboveStart;
    }
    return {
      left: clampValue(rightStart, hMin, Math.max(hMin, hMax)),
      top: clampValue(top, vMin, Math.max(vMin, vMax)),
    };
  }

  // Right is tight. Centring on the point pushes a wide box past the leading edge
  // (the leftmost month sits at pointX ~ the panel's own left), so keep the box on
  // the right of the point and only fall back to the left when there is more room
  // there — then clamp. Either way the box stays inside [hMin, hMax].
  const leftStart = pointX - boxWidth - FLOATING_TOOLTIP_GAP;
  const roomRight = horizontalMax - pointX;
  const roomLeft = pointX - horizontalMin;
  const horizontalStart = roomLeft > roomRight ? leftStart : rightStart;

  return {
    left: clampValue(horizontalStart, hMin, Math.max(hMin, hMax)),
    top: clampValue(pointY + FLOATING_TOOLTIP_GAP, vMin, Math.max(vMin, vMax)),
  };
};

const FloatingTooltip: FC<{
  x: number;
  y: number;
  contentKey: string | number;
  isRTL?: boolean;
  /** Arabic spending card: open right of (or below) the point — never to the left. */
  rtlOpenRightOrBelow?: boolean;
  /** When set, the tooltip is clamped inside this chart card instead of the full viewport. */
  boundsRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
}> = ({ x, y, contentKey, isRTL = false, rtlOpenRightOrBelow = false, boundsRef, children }) => {
  const boxRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) {
      return;
    }

    // `x`/`y` are the hovered point's `activeCoordinate` (SVG-relative) converted to
    // viewport pixels via the chart wrapper's `getBoundingClientRect()` — see the
    // chart's `onMouseMove` handlers. Pinning to the point rather than the raw cursor
    // keeps the box still while the mouse moves within the same point's hover zone.
    // The wrapper under-reports its size: `width: max-content` around a flex card whose
    // `overflow: hidden` collapses that intrinsic width. Measure the card that paints.
    const contentEl = (box.firstElementChild ?? box) as HTMLElement;
    const contentRect = contentEl.getBoundingClientRect();
    const boxWidthMeasured = Math.max(
      box.offsetWidth,
      contentEl.scrollWidth,
      Math.ceil(contentRect.width),
    );
    const boxHeightMeasured = Math.max(
      box.offsetHeight,
      contentEl.scrollHeight,
      Math.ceil(contentRect.height),
    );
    const boxWidth = rtlOpenRightOrBelow ? boxWidthMeasured + 16 : boxWidthMeasured;
    const boxHeight = rtlOpenRightOrBelow ? boxHeightMeasured + 12 : boxHeightMeasured;
    // Unlike `window.inner*`, these exclude the scrollbar gutters.
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const pointX = x;
    const pointY = y;

    // The sidebar owns the viewport's leading edge, so clamping to 0 leaves the box
    // under it (as in InstagramFollowersReport's `getCursorTooltipPosition`). The
    // `.ant-layout-sider` part skips the mobile drawer, which reuses `.sidebar`.
    let viewportLeftEdge = 0;
    let viewportRightEdge = viewportWidth;
    const sidebarRect = document
      .querySelector(".sidebar.ant-layout-sider")
      ?.getBoundingClientRect();
    if (sidebarRect && sidebarRect.width > 0) {
      if (sidebarRect.left <= 1) {
        viewportLeftEdge = sidebarRect.right;
      } else if (sidebarRect.right >= viewportWidth - 1) {
        viewportRightEdge = sidebarRect.left;
      }
    }

    const boundsRect = boundsRef?.current?.getBoundingClientRect();
    const horizontalMin = boundsRect
      ? Math.max(viewportLeftEdge, boundsRect.left)
      : viewportLeftEdge;
    const horizontalMax = boundsRect
      ? Math.min(viewportRightEdge, boundsRect.right)
      : viewportRightEdge;
    const verticalMin = boundsRect ? Math.max(0, boundsRect.top) : 0;
    const verticalMax = boundsRect
      ? Math.min(viewportHeight, boundsRect.bottom)
      : viewportHeight;
    const preferBeforeHorizontal = boundsRef ? false : isRTL;
    // In RTL, push the tooltip further right of the point than the LTR default gap
    // so it clears the point/axis instead of hugging it.
    const horizontalGap = isRTL && !preferBeforeHorizontal ? FLOATING_TOOLTIP_GAP + 24 : undefined;

    const placement =
      rtlOpenRightOrBelow
        ? placeRtlRightOrBelow(
            pointX,
            pointY,
            boxWidth,
            boxHeight,
            horizontalMin,
            horizontalMax,
            verticalMin,
            verticalMax,
          )
        : {
            left: placeAlongAxis(
              pointX,
              boxWidth,
              horizontalMin,
              horizontalMax,
              preferBeforeHorizontal,
              horizontalGap,
            ),
            top: placeAlongAxis(pointY, boxHeight, verticalMin, verticalMax),
          };

    // Cap the box to the room actually available so an under-measured
    // `width: max-content` box cannot spill past the clamped edge.
    const availableWidth = Math.max(120, horizontalMax - horizontalMin - FLOATING_TOOLTIP_MARGIN * 2);
    box.style.setProperty("max-width", `${availableWidth}px`);
    box.style.setProperty("left", `${placement.left}px`);
    box.style.setProperty("top", `${placement.top}px`);
    box.classList.add("cltv-dashboard__floating-tooltip--placed");

    return () => {
      box.classList.remove("cltv-dashboard__floating-tooltip--placed");
    };
  }, [x, y, contentKey, isRTL, rtlOpenRightOrBelow, boundsRef]);

  // Portalled like DefinitionTooltip: `position: fixed` stops being viewport-relative
  // once any ancestor gains a `transform`/`filter`/`will-change`.
  return createPortal(
    <div
      ref={boxRef}
      // `cltv-dashboard` keeps the scoped `.analytics-chart-tooltip` styles matching.
      className={[
        "cltv-dashboard",
        "cltv-dashboard__floating-tooltip",
        boundsRef ? "cltv-dashboard__floating-tooltip--chart-panel" : "",
        rtlOpenRightOrBelow ? "cltv-dashboard__floating-tooltip--spending-panel" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>,
    document.body,
  );
};

const insightIcon = (tone: InsightDisplay["tone"]) => {
  if (tone === "warning") {
    return <WarningOutlined />;
  }
  return <TrendingUp size={16} />;
};

const normalizeCurrencyLabel = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
};

/** Pull the currency code/symbol off the front of a backend-formatted value, e.g. "USD 92.98" -> "USD". */
const extractCurrencyCode = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const str = String(value).trim();
  const codeMatch = str.match(/^([A-Za-z]{2,5})\s/);
  if (codeMatch) {
    return codeMatch[1].toUpperCase();
  }
  const symbolMatch = str.match(/^(\$|Rs\.?|₨|₦|€|£)/);
  return symbolMatch ? symbolMatch[1] : null;
};

/** Prefer the backend's formattedValue; fall back to client-side formatting of the raw number. */
const formatMoneyCell = (
  formattedValue: string | null | undefined,
  raw: number | null | undefined,
  formatFallback: (value: number) => string,
): string | null => {
  if (formattedValue) {
    return normalizeCurrencyLabel(formattedValue);
  }
  if (raw === null || raw === undefined) {
    return null;
  }
  return formatFallback(raw);
};

const escapeCsvCell = (value: string | number | null | undefined) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, "\"\"")}"` : text;
};

/** Removes LEFT-TO-RIGHT ISOLATE / POP DIRECTIONAL ISOLATE marks (see `wrapBidiIsolate`
 * in numberFormat.js) that Excel renders as visible placeholder glyphs in CSV cells. */
const stripBidiIsolateMarks = (value: string) => value.replace(/[⁦⁩]/g, "");

// Excel auto-detects each unquoted CSV cell's type by sniffing its text; a cell mixing
// digits with Arabic words (e.g. "٢ سنة") can get misread as a date/number format and
// render as garbled leftover format characters. Forcing quotes keeps it as plain text
// without affecting numeric columns elsewhere, which still need to stay real numbers.
const forceCsvTextCell = (value: string) => `"${value.replace(/"/g, "\"\"")}"`;

const isChurnKpiCard = (card: CLTVKPICard) =>
  card.id === "churn-risk" || card.id === "churn_risk" || /churn/i.test(card.id);

const getKpiIcon = (card: CLTVKPICard) => {
  const normalized = `${card.id} ${card.title}`.toLowerCase();

  if (normalized.includes("churn")) {
    return <AlertTriangle size={22} />;
  }

  const Icon = getDashboardKpiLucideIconComponent(`${card.id} ${card.title}`);
  return <Icon size={22} />;
};

const progressFromPercentValue = (value: string | number) => {
  const m = String(value).match(/([-+]?\d+\.?\d*)/);
  if (!m) {
    return 72;
  }
  const n = Math.abs(parseFloat(m[1]));
  if (!Number.isFinite(n)) {
    return 72;
  }
  return Math.min(100, Math.max(8, n));
};

/** Customer age bucketed to whole years, falling back to months below one year. */
const getCustomerLifetimeLabel = (monthsActive: number, t: TFunction, language: string) => {
  const wholeYears = Math.min(4, Math.floor(monthsActive / 12));
  if (wholeYears >= 1) {
    return t(wholeYears === 1 ? "cltv.table.lifetimeYear" : "cltv.table.lifetimeYears", {
      value: formatNumber(wholeYears, {}, language),
    });
  }
  return t("cltv.table.lifetimeMonths", {
    value: formatNumber(Math.max(1, monthsActive), {}, language),
  });
};

const getTotalOrders = (row: CustomerAnalyticsRow) =>
  row.totalOrdersSince2023
  ?? ((row.orders_0_30d ?? 0) + (row.orders_30_60d ?? 0) + (row.orders_60_90d ?? 0));

const getTotalSpend = (row: CustomerAnalyticsRow) =>
  row.totalSpendSince2023
  ?? ((row.spend_0_30d ?? 0) + (row.spend_30_60d ?? 0) + (row.spend_60_90d ?? 0));

const getPredictedOrders = (row: CustomerAnalyticsRow) =>
  row.predictedOrders3m
  ?? ((row.predOrders_0_30d ?? 0) + (row.predOrders_30_60d ?? 0));

const getPredictedSpend = (row: CustomerAnalyticsRow) =>
  row.predictedSpend3m
  ?? ((row.predSpend_0_30d ?? 0) + (row.predSpend_30_60d ?? 0));

const getPredictedCltv = (row: CustomerAnalyticsRow) =>
  row.predictedLtv90d ?? (getTotalSpend(row) + getPredictedSpend(row));

/** `cltv.segments.*` leaf that renders each tone, so the pills reuse the segment-name copy. */
const CUSTOMER_SEGMENT_TONE_I18N_LEAF: Record<CustomerSegmentTone, string> = {
  loyal: "loyal",
  "high-spender": "highSpender",
  "likely-to-churn": "likelyToChurn",
  "one-timer": "oneTimer",
  idle: "idle",
  indecisive: "indecisive",
  "cart-abandoner": "cartAbandoners",
  unsubscribed: "unsubscribed",
};

const CUSTOMER_SEGMENT_TONES = Object.keys(
  CUSTOMER_SEGMENT_TONE_I18N_LEAF
) as CustomerSegmentTone[];

const SEGMENT_SLUG_TO_TONE: Record<string, CustomerSegmentTone> = {
  loyal: "loyal",
  high_spender: "high-spender",
  high_spenders: "high-spender",
  likely_to_churn: "likely-to-churn",
  one_timer: "one-timer",
  one_timers: "one-timer",
  idle: "idle",
  cart_abandoner: "cart-abandoner",
  cart_abandoners: "cart-abandoner",
  unsubscribed: "unsubscribed",
  indecisive: "indecisive",
  indecisive_buyer: "indecisive",
  indecisive_buyers: "indecisive",
};

/**
 * Resolves a segment value returned by the API to its tone. The API localizes
 * `customer-analytics[].segments`, so the English slug table alone matches nothing under
 * `lang=ar` and every Arabic row would silently fall through to the heuristic below —
 * showing different segments than the same row shows in English. Localized labels are
 * therefore matched too, via the `cltv.segments.*` copy this dashboard renders for each tone.
 *
 * ⚠️ That second pass does NOT currently rescue Arabic: the backend translates these labels from
 * its own `locales/ar.json`, and its wording differs from ours for 7 of the 8 segments (only
 * `unsubscribed` happens to match) — verified against the segment labels quoted in the backend's
 * "CLTV & Organic Media — Arabic formatting" reference. Arabic rows still reach the heuristic, so
 * their pills can disagree with the English ones. Matching more strings would only couple us to
 * another service's copy.
 *
 * TODO(backend): `customer-analytics[].segments` should expose the stable English ids (the same
 * ones the `segment` filter param accepts) — as a `{id, label}` pair, which the backend has
 * already offered for `customer-analytics/segments`. That is the only fix that closes this, and it
 * would also let `fetchCLTVKpis` drop its companion `lang=en` request for segment colors.
 */
const buildSegmentToneResolver = (t: TFunction) => {
  const localizedToTone = new Map<string, CustomerSegmentTone>();
  for (const tone of CUSTOMER_SEGMENT_TONES) {
    const label = t(`cltv.segments.${CUSTOMER_SEGMENT_TONE_I18N_LEAF[tone]}`, {
      defaultValue: "",
    });
    if (label) {
      localizedToTone.set(label.trim().toLowerCase(), tone);
    }
  }

  return (segment: string): CustomerSegmentTone | null =>
    SEGMENT_SLUG_TO_TONE[cltvSegmentNameSlug(segment)] ??
    localizedToTone.get(String(segment ?? "").trim().toLowerCase()) ??
    null;
};

const getCustomerSegmentTone = (
  row: CustomerAnalyticsRow,
  normalizeCustomerSegmentTone: (_segment: string) => CustomerSegmentTone | null
): CustomerSegmentTone[] => {
  const totalOrders = getTotalOrders(row);
  const totalSpend = getTotalSpend(row);
  const predictedCltv = getPredictedCltv(row);
  const backendSegments = (Array.isArray(row.segment) ? row.segment : row.segment ? [row.segment] : [])
    .map(normalizeCustomerSegmentTone)
    .filter((segment): segment is CustomerSegmentTone => Boolean(segment));

  if (backendSegments.length > 0) {
    return Array.from(new Set(backendSegments)).slice(0, 3);
  }

  const tones: CustomerSegmentTone[] = [];

  if ((row.segmentLoyal ?? 0) > 0 || totalOrders >= 5) {
    tones.push("loyal");
  }

  if ((row.segmentHighSpenders ?? 0) > 0 || totalSpend >= 1500 || predictedCltv >= 1500) {
    tones.push("high-spender");
  }

  if ((row.segmentOneTimers ?? 0) > 0 || (totalOrders <= 1 && totalSpend > 0)) {
    tones.push("one-timer");
  }

  if ((row.segmentLikelyToChurn ?? 0) > 0 || row.likelyToChurn || totalOrders === 0 && predictedCltv > 0) {
    tones.push("likely-to-churn");
  }

  if ((row.segmentCartAbandoners ?? 0) > 0) {
    tones.push("cart-abandoner");
  }

  if ((row.segmentUnsubscribed ?? 0) > 0) {
    tones.push("unsubscribed");
  }

  if ((row.segmentIndecisive ?? 0) > 0) {
    tones.push("indecisive");
  }

  if ((row.segmentIdle ?? 0) > 0 || (totalOrders === 0 && totalSpend === 0 && predictedCltv === 0)) {
    tones.push("idle");
  }

  if (tones.length === 0) {
    tones.push("indecisive");
  }

  return tones.slice(0, 3);
};

const getCustomerSegmentLabel = (tone: CustomerSegmentTone, t: TFunction) =>
  t(`cltv.segments.${CUSTOMER_SEGMENT_TONE_I18N_LEAF[tone]}`);


const SEGMENT_EVOLUTION_SERIES: Array<{ key: EvolutionSeriesKey; labelKey: string; color: string }> = [
  { key: "loyal", labelKey: "cltv.segments.loyal", color: CHART_COLOR_PALETTE[0] },
  { key: "highSpender", labelKey: "cltv.segments.highSpender", color: CHART_COLOR_PALETTE[1] },
  { key: "likelyToChurn", labelKey: "cltv.segments.likelyToChurn", color: CHART_COLOR_PALETTE[2] },
  { key: "idle", labelKey: "cltv.segments.idle", color: CHART_COLOR_PALETTE[3] },
  { key: "oneTimer", labelKey: "cltv.segments.oneTimer", color: CHART_COLOR_PALETTE[4] },
  { key: "indecisive", labelKey: "cltv.segments.indecisive", color: CHART_COLOR_PALETTE[5] },
  { key: "cartAbandoners", labelKey: "cltv.segments.cartAbandoners", color: CHART_COLOR_PALETTE[6] },
  { key: "unsubscribed", labelKey: "cltv.segments.unsubscribed", color: CHART_COLOR_PALETTE[7] },
];

const mapPredictiveInsight = (insight: PredictiveInsightItem): InsightDisplay => {
  let tone: InsightDisplay["tone"] = "info";
  if (insight.id === "churn-risk-alert" || insight.id === "churn-alert") {
    tone = "warning";
  } else if (insight.id === "revenue-growth") {
    tone = "positive";
  }

  let progress = 70;
  if (insight.id === "revenue-growth") {
    progress = progressFromPercentValue(insight.value ?? 0);
  } else if (insight.id === "churn-risk-alert" || insight.id === "churn-alert") {
    progress = 64;
  } else if (insight.id === "upsell-opportunities") {
    progress = 74;
  }

  return {
    key: insight.id,
    title: insight.title,
    subtitle: insight.description ?? "",
    value: insight.value ?? "",
    badge: insight.badge ?? "",
    progress,
    tone,
    raw: insight,
  };
};

const getDefaultMonthRange = () => {
  const now = dayjs();
  const end = now.startOf("month").format("YYYY-MM-DD");
  const start = now.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
  return { start, end };
};

const CLTVDashboard: FC = () => {
  const { t, i18n } = useTranslation();
  const apiLang = getApiLangFromI18n(i18n);
  const isAr = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase().startsWith("ar");
  const moneyLocale = isAr ? "ar-SA" : undefined;
  const formatMoney = useCallback(
    (value: number) =>
      `SAR ${value.toLocaleString(moneyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    [moneyLocale],
  );
  const formatMoneyShort = useCallback(
    (value: number) => {
      const compact = (scaled: number, suffix: string) => {
        const fractionDigits = scaled % 1 === 0 ? 0 : 1;
        return `SAR ${scaled.toLocaleString(moneyLocale, {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        })}${suffix}`;
      };

      const abs = Math.abs(value);
      if (abs >= 1_000_000) {
        return compact(value / 1_000_000, "M");
      }
      if (abs >= 1_000) {
        return compact(value / 1_000, "K");
      }
      return `SAR ${value.toLocaleString(moneyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    [moneyLocale],
  );
  const formatKLabel = useCallback(
    (value: number) => {
      if (value === 0) {
        return (0).toLocaleString(moneyLocale);
      }
      if (Math.abs(value) >= 1000000) {
        const millions = value / 1000000;
        const fractionDigits = millions % 1 === 0 ? 0 : 1;
        return `${millions.toLocaleString(moneyLocale, {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        })}M`;
      }
      return `${Math.round(value / 1000).toLocaleString(moneyLocale)}K`;
    },
    [moneyLocale],
  );
  const formatSpendingBucket = useCallback(
    (raw: string) => {
      const slug = cltvSegmentNameSlug(raw.replace(/[()[\]]/g, " "));
      const tk = `cltv.spendingBuckets.${slug}`;
      const translated = t(tk, { defaultValue: "" });
      if (translated && translated !== tk) {
        return translated;
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        const d = new Date(`${raw.slice(0, 10)}T12:00:00`);
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleDateString(isAr ? "ar-SA" : "en-US", {
            month: "short",
            year: "numeric",
          });
        }
      }
      return raw;
    },
    [t, isAr],
  );
  const formatMonthYear = useCallback(
    (value: string) =>
      toLocaleDigits((isAr ? dayjs(value).locale("ar") : dayjs(value)).format("MMM YYYY"), isAr),
    [isAr],
  );
  const formatCount = useCallback(
    (value: number | null | undefined) =>
      value === null || value === undefined ? "" : formatNumber(value, {}, i18n.language),
    [i18n.language],
  );
  // Unisolated so Arabic renders the `%` on the left of the digits, matching the KPI trend
  // percentages and the Sales & Products cards.
  const formatPercent = useCallback(
    (value: number | null | undefined) =>
      value === null || value === undefined
        ? ""
        : `${formatNumberDigits(value, { maximumFractionDigits: 1 }, i18n.language)}%`,
    [i18n.language],
  );
  const emptyCell = t("cltv.table.notAvailable");
  const customerTableTooltip = useMemo(() => getCltvSectionDefinition("customerTable", t), [t]);
  const columnTooltips = useMemo(
    () => ({
      profile: getCltvColumnDefinition("profile", t),
      totalSpend: getCltvColumnDefinition("totalSpend", t),
      totalOrders: getCltvColumnDefinition("totalOrders", t),
      predictedSpend: getCltvColumnDefinition("predictedSpend", t),
      predictedOrders: getCltvColumnDefinition("predictedOrders", t),
      segments: getCltvColumnDefinition("segments", t),
    }),
    [t],
  );
  const resolveSegmentTone = useMemo(() => buildSegmentToneResolver(t), [t]);
  const segmentTonesForRow = useCallback(
    (row: CustomerAnalyticsRow) => getCustomerSegmentTone(row, resolveSegmentTone),
    [resolveSegmentTone],
  );
  const defaultRange = getDefaultMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [tableSort, setTableSort] = useState<{
    key: CustomerTableSortKey;
    direction: SortDirection;
  } | null>(null);
  const pageSize = 10;
  // Actual vs Predicted Revenue always shows a fixed 6-month window and isn't
  // affected by the date-range filter, so once it has loaded once we keep it
  // visible during background refetches instead of re-showing the spinner —
  // that flicker made it look like the date filter was changing this chart.
  const [hasLoadedSpendingPatterns, setHasLoadedSpendingPatterns] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, debouncedSearch, selectedSegments, tableSort]);

  const apiSortBy = tableSort ? SORT_KEY_TO_API_FIELD[tableSort.key] : undefined;
  const apiSortDir = tableSort ? tableSort.direction : undefined;
  const apiSegment = selectedSegments.length > 0 ? selectedSegments : undefined;

  const {
    kpiCards,
    segmentDistribution,
    predictiveInsights,
    spendingPatterns,
    segmentEvolution,
    customerAnalytics,
    kpiLoading,
    kpiError,
    chartsError,
    customerLoading,
    customerError,
  } = useCLTVDashboard({
    startDate,
    endDate,
    lang: apiLang,
    page,
    pageSize,
    search: debouncedSearch,
    segment: apiSegment,
    sortBy: apiSortBy,
    sortDir: apiSortDir,
  });

  useEffect(() => {
    if (spendingPatterns.length > 0) {
      setHasLoadedSpendingPatterns(true);
    }
  }, [spendingPatterns]);

  const narrativeCurrency = useMemo(
    () =>
      extractCurrencyCode(kpiCards[0]?.formattedValue) ??
      extractCurrencyCode(spendingPatterns[0]?.predictedFormattedValue ?? spendingPatterns[0]?.actualFormattedValue) ??
      undefined,
    [kpiCards, spendingPatterns],
  );

  const narrativePayloadData = useMemo<Record<string, unknown>>(
    () => ({
      kpis: {
        kpiCards,
        segmentDistribution,
        predictiveInsights,
      },
      spending_patterns: {
        ...(narrativeCurrency ? { currency: narrativeCurrency } : {}),
        currentMonth: endDate,
        data: spendingPatterns,
      },
      segment_evolution: {
        data: segmentEvolution,
      },
    }),
    [kpiCards, segmentDistribution, predictiveInsights, spendingPatterns, segmentEvolution, endDate, narrativeCurrency],
  );

  const { narrative: cltvNarrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "cltv_dashboard",
    tabKey: "overview",
    startDate,
    endDate,
    data: narrativePayloadData,
    enabled: !kpiLoading && !kpiError && Boolean(kpiCards.length || segmentDistribution.length),
  });

  const pieData = useMemo(() => {
    return [...segmentDistribution]
      .filter(segment => (segment.count ?? 0) > 0)
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.name.localeCompare(b.name))
      .map(s => {
        // `name` is localized by the API, so only `nameKey` (its canonical English name) can key
        // the palette and the `cltv.segmentNames.*` copy — keeping the Arabic bars colored and
        // labelled exactly like the English ones.
        const segmentKey = s.nameKey ?? s.name;

        return {
          segmentKey,
          name: t(`cltv.segmentNames.${cltvSegmentNameSlug(segmentKey)}`, { defaultValue: s.name }),
          value: s.count,
          countFormattedValue: s.countFormattedValue,
          percent: s.percent,
          color: CLTV_SEGMENT_COLORS[segmentKey] ?? defaultCltvSegmentColor,
        };
      });
  }, [segmentDistribution, t]);

  const insightRows = useMemo(() => predictiveInsights.map(mapPredictiveInsight), [predictiveInsights]);

  const pagination = customerAnalytics?.pagination;
  const customerRows = useMemo(() => customerAnalytics?.data ?? [], [customerAnalytics]);

  // Sorting and segment filtering are handled server-side; customerRows is already the correct page.
  const sortedCustomerRows = customerRows;

  const totalRecords = pagination?.totalRecords ?? 0;

  const customerTableTotalPages = useMemo(() => {
    if (pagination && typeof pagination.totalPages === "number" && pagination.totalPages > 0) {
      return pagination.totalPages;
    }
    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }, [pagination, totalRecords, pageSize]);

  const rangeSummary = useMemo(() => {
    // Dates keep the numeric DD-MM-YYYY form in both locales (only the digits are localized),
    // so the two months stay directly comparable at a glance.
    const from = toLocaleDigits(dayjs(startDate).format("DD-MM-YYYY"), isAr);
    const to = toLocaleDigits(dayjs(endDate).format("DD-MM-YYYY"), isAr);
    return `${from} ${t("cltv.common.vs")} ${to}`;
  }, [startDate, endDate, isAr, t]);

  const maxObservedSpend = useMemo(() => {
    if (!customerRows.length) {
      return 1;
    }

    const biggest = Math.max(...customerRows.map(row => getTotalSpend(row)));

    return biggest > 0 ? biggest : 1;
  }, [customerRows]);

  const spendingChartData = useMemo(() => {
    // Anchored on the real current date, not the date-range filter — this panel
    // always shows a fixed 6-month window and must not shift when the user
    // changes the primary/secondary month filter.
    const currentMonth = isAr ? dayjs().locale("ar") : dayjs();
    const timeline = Array.from({ length: 6 }, (_, index) => currentMonth.subtract(3 - index, "month"));
    const spendingByMonth = new Map(
      spendingPatterns.map(item => {
        const rawMonth = item.month ?? item.bucket ?? "";
        return [dayjs(rawMonth).format("YYYY-MM"), item];
      }),
    );
    const historicalGaps = timeline
      .map((monthPoint, index) => {
        if (index >= 3) {
          return null;
        }
        const item = spendingByMonth.get(monthPoint.format("YYYY-MM"));
        if (!item) {
          return null;
        }
        const predicted = Number(item.predicted) || 0;
        const actual = Number(item.actual) || 0;
        return actual > 0 ? Math.abs(actual - predicted) : null;
      })
      .filter((value): value is number => value !== null);
    const averageHistoricalGap = historicalGaps.length
      ? historicalGaps.reduce((sum, value) => sum + value, 0) / historicalGaps.length
      : 0;

    return timeline.map((monthPoint, index) => {
      const key = monthPoint.format("YYYY-MM");
      const item = spendingByMonth.get(key);
      const predicted = Number(item?.predicted) || 0;
      const actualRaw = Number(item?.actual) || 0;
      const isHistorical = index < 3;
      const isCurrent = index === 3;
      const actual = isHistorical && actualRaw > 0 ? actualRaw : null;
      const comparisonValues =
        actual !== null ? [predicted, actual] : [predicted];
      const pointMin = Math.min(...comparisonValues);
      const pointMax = Math.max(...comparisonValues);
      const spread = pointMax - pointMin;
      const preferredGap = averageHistoricalGap > 0 ? averageHistoricalGap * 0.35 : pointMax * 0.045;
      const padding = Math.max(preferredGap, spread * 0.18, 18000);

      return {
        label: monthPoint.format("MMM"),
        fullLabel: toLocaleDigits(monthPoint.format("MMM YYYY"), isAr),
        predicted,
        actual,
        predictedRaw: item ? predicted : null,
        actualRaw: actual,
        predictedFormattedValue: item?.predictedFormattedValue,
        actualFormattedValue: actual !== null ? item?.actualFormattedValue : undefined,
        bandLower: Math.max(0, pointMin - padding),
        bandUpper: pointMax + padding,
        status: isHistorical ? "historical" : isCurrent ? "current" : "forecast",
      };
    });
  }, [isAr, spendingPatterns]);

  const spendingChartContainerRef = useRef<HTMLDivElement>(null);
  const spendingPanelRef = useRef<HTMLElement>(null);
  const [spendingHover, setSpendingHover] = useState<{ x: number; y: number; index: number } | null>(
    null,
  );
  const handleSpendingChartMouseMove = useCallback((state: unknown) => {
    const chartState = state as {
      isTooltipActive?: boolean;
      activeTooltipIndex?: number;
      activeCoordinate?: { x?: number; y?: number };
    } | null;
    const container = spendingChartContainerRef.current;
    if (
      chartState?.isTooltipActive &&
      typeof chartState.activeTooltipIndex === "number" &&
      typeof chartState.activeCoordinate?.x === "number" &&
      typeof chartState.activeCoordinate?.y === "number" &&
      container
    ) {
      // Pin the tooltip to the hovered data point (`activeCoordinate`, SVG-relative)
      // rather than the raw cursor — the box shouldn't drift while the mouse moves
      // around within the same point's hover zone. Converted to viewport pixels via
      // the chart container's own rect since `FloatingTooltip` positions with `fixed`.
      const point = getChartHoverViewportPoint(container, {
        x: chartState.activeCoordinate!.x!,
        y: chartState.activeCoordinate!.y!,
      });
      setSpendingHover(prev => {
        if (prev?.index === chartState.activeTooltipIndex) {
          return prev;
        }
        return {
          x: point.x,
          y: point.y,
          index: chartState.activeTooltipIndex!,
        };
      });
    }
  }, []);
  const handleSpendingChartMouseLeave = useCallback(() => setSpendingHover(null), []);

  const spendingYAxisDomain = useMemo(() => {
    const values = spendingChartData.flatMap(point => [
      point.predicted,
      point.actual ?? null,
    ]).filter((value): value is number => value !== null && Number.isFinite(value));

    if (!values.length) {
      return [0, 100] as [number, number];
    }

    const max = Math.max(...values);
    const upperBound = max * 1.15;

    return [0, upperBound] as [number, number];
  }, [spendingChartData]);

  const spendingSubtitle = useMemo(() => {
    const formatted = (isAr ? dayjs().locale("ar") : dayjs()).format("MMMM YYYY");
    return t("cltv.charts.sixMonthView", { month: toLocaleDigits(formatted, isAr) });
  }, [isAr, t]);

  const formatCountAxis = useCallback(
    (value: number) => {
      if (value === 0) {
        return (0).toLocaleString(moneyLocale);
      }
      if (Math.abs(value) >= 1000) {
        return `${Math.round(value / 1000).toLocaleString(moneyLocale)}K`;
      }
      return value.toLocaleString(moneyLocale);
    },
    [moneyLocale],
  );

  const activeEvolutionSeries = useMemo(() => {
    const visible = SEGMENT_EVOLUTION_SERIES
      .map(series => ({
        ...series,
        latestValue: segmentEvolution[segmentEvolution.length - 1]?.[series.key] ?? 0,
        maxValue: Math.max(...segmentEvolution.map(point => point[series.key] ?? 0)),
      }))
      .filter(series => series.maxValue > 0)
      .sort((a, b) => b.latestValue - a.latestValue);

    return visible;
  }, [segmentEvolution]);

  const evolutionChartContainerRef = useRef<HTMLDivElement>(null);
  const [evolutionHover, setEvolutionHover] = useState<{ x: number; y: number; index: number } | null>(
    null,
  );
  const handleEvolutionChartMouseMove = useCallback((state: unknown) => {
    const chartState = state as {
      isTooltipActive?: boolean;
      activeTooltipIndex?: number;
      activeCoordinate?: { x?: number; y?: number };
    } | null;
    const container = evolutionChartContainerRef.current;
    if (
      chartState?.isTooltipActive &&
      typeof chartState.activeTooltipIndex === "number" &&
      typeof chartState.activeCoordinate?.x === "number" &&
      typeof chartState.activeCoordinate?.y === "number" &&
      container
    ) {
      const point = getChartHoverViewportPoint(container, {
        x: chartState.activeCoordinate!.x!,
        y: chartState.activeCoordinate!.y!,
      });
      setEvolutionHover(prev => {
        if (prev?.index === chartState.activeTooltipIndex) {
          return prev;
        }
        return {
          x: point.x,
          y: point.y,
          index: chartState.activeTooltipIndex!,
        };
      });
    }
  }, []);
  const handleEvolutionChartMouseLeave = useCallback(() => setEvolutionHover(null), []);
  const evolutionTooltipPayload = useMemo(() => {
    if (!evolutionHover) {
      return null;
    }
    const point = segmentEvolution[evolutionHover.index];
    if (!point) {
      return null;
    }
    return {
      label: point.label,
      payload: activeEvolutionSeries.map(series => ({
        name: series.key,
        value: Number(point[series.key] ?? 0),
        color: series.color,
      })),
    };
  }, [evolutionHover, segmentEvolution, activeEvolutionSeries]);

  useEffect(() => {
    if (!spendingHover && !evolutionHover) {
      return;
    }
    const clearHovers = () => {
      setSpendingHover(null);
      setEvolutionHover(null);
    };
    window.addEventListener("scroll", clearHovers, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", clearHovers, { capture: true });
  }, [spendingHover, evolutionHover]);

  const [isExporting, setIsExporting] = useState(false);

  const handleExportCustomerCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      // The backend allows page_size up to 1000 for this endpoint. Page through
      // the results in chunks and accumulate the rows to keep memory bounded
      // for very large datasets while still cutting down the number of requests.
      const EXPORT_PAGE_SIZE = 1000;
      const allRows: CustomerAnalyticsRow[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const res = await fetchCLTVCustomerAnalytics({
          primary_date: endDate,
          lang: apiLang,
          page,
          page_size: EXPORT_PAGE_SIZE,
          search: debouncedSearch.trim() || undefined,
          segment: apiSegment,
          sort_by: apiSortBy,
          sort_order: apiSortDir,
        });
        allRows.push(...res.data);
        totalPages = res.pagination.totalPages;
        page += 1;
      } while (page <= totalPages);

      const headers = [
        t("cltv.headers.customerName"),
        t("cltv.headers.customerEmail"),
        t("cltv.headers.customerProfile"),
        t("cltv.headers.totalSpend"),
        t("cltv.headers.totalOrders"),
        t("cltv.headers.predictedSpend3m"),
        t("cltv.headers.predictedOrders3m"),
        t("cltv.headers.predictedCltv"),
        t("cltv.headers.segments"),
      ];
      const rows = allRows.map(row => {
        const totalOrders = getTotalOrders(row);
        const totalSpend = getTotalSpend(row);
        const predictedOrders = getPredictedOrders(row);
        const predictedSpend = getPredictedSpend(row);
        const predictedCltv = getPredictedCltv(row);
        const monthsActive = Math.max(1, row.customerAgeMonths ?? totalOrders * 6);

        const lifetimeLabelIndex = 2;
        return {
          cells: [
            row.customerName ?? row.customerId,
            row.email || emptyCell,
            getCustomerLifetimeLabel(monthsActive, t, i18n.language),
            formatMoneyCell(row.totalSpendFormattedValue, totalSpend, formatMoney) ?? emptyCell,
            formatNumberDigits(totalOrders, {}, i18n.language),
            formatMoneyCell(row.predictedSpend3mFormattedValue, predictedSpend, formatMoney) ?? emptyCell,
            formatNumberDigits(predictedOrders, {}, i18n.language),
            formatMoneyCell(row.predictedLtv90dFormattedValue, predictedCltv, formatMoney) ?? emptyCell,
            segmentTonesForRow(row)
              .map(tone => getCustomerSegmentLabel(tone, t))
              .join("; ") || emptyCell,
          ],
          lifetimeLabelIndex,
        };
      });
      // Bidi isolate marks (U+2066/U+2069) keep numeral runs stable in RTL browser text
      // (added client-side by formatNumber/toLocaleDigits, or already present in
      // backend-formatted values like *FormattedValue fields) — but Excel's CSV reader
      // shows them as literal placeholder glyphs instead of hiding them. CSV cells don't
      // need bidi help since Excel lays out RTL text itself, so strip them at export time.
      const csv = [
        headers.map(escapeCsvCell).join(","),
        ...rows.map(({ cells, lifetimeLabelIndex }) =>
          cells
            .map((cell, index) => {
              const plain = stripBidiIsolateMarks(String(cell ?? ""));
              return index === lifetimeLabelIndex
                ? forceCsvTextCell(plain)
                : escapeCsvCell(plain);
            })
            .join(","),
        ),
      ].join("\r\n");
      // Excel on Windows opens CSV as ANSI unless a UTF-8 BOM is present; Arabic then
      // renders as mojibake (e.g. Ø§Ù„Ù…). English exports are unchanged without the BOM.
      const csvPayload = apiLang === "ar" ? `\uFEFF${csv}` : csv;
      const blob = new Blob([csvPayload], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cltv-customer-analytics-${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Never let an export failure surface as an uncaught runtime error.
      console.error("CLTV customer export failed", err);
      window.alert(t("cltv.errors.loadCustomerFailed"));
    } finally {
      setIsExporting(false);
    }
  }, [
    endDate,
    apiLang,
    debouncedSearch,
    apiSegment,
    apiSortBy,
    apiSortDir,
    formatMoney,
    segmentTonesForRow,
    emptyCell,
    i18n.language,
    t,
  ]);

  return (
    <div className="cltv-dashboard-layout">
      <DashboardSectionHeader
        className="cltv-dashboard__top"
        title={t("cltv.title")}
        subtitle={`${t("cltv.subtitle")} • ${rangeSummary}`}
        actionsClassName="dashboard-section-header__actions--filters"
        actions={
          <div className="dashboard-section-header__control dashboard-section-header__control--wide">
            <MonthRangePicker
              startDate={startDate}
              endDate={endDate}
              onRangeChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              allowUnlimitedHistory
              startPlaceholder={t("monthRangePicker.secondaryMonth")}
              endPlaceholder={t("monthRangePicker.primaryMonth")}
            />
          </div>
        }
      />
      <div className="cltv-dashboard cltv-dashboard__main">

        {kpiError ? (
          <p className="cltv-dashboard__error-banner" role="alert">
            {kpiError}
          </p>
        ) : null}

        <DashboardNarrativePanel
          narrative={cltvNarrative}
          loading={kpiLoading || narrativeLoading}
          aiSummary={aiSummary}
        />

        <section className="cltv-dashboard__kpi-grid">
          {kpiLoading ? (
            <div className="cltv-dashboard__kpi-loading">
              <LoadingSpinner compact />
            </div>
          ) : (
            kpiCards.map((card, index) => {
              const kSlug = cltvIdSlug(card.id);
              const kTitle = resolveKpiLabel(t, `cltv.kpi.${kSlug}.title`, card.title);
              const kSubtitle = resolveKpiLabel(t, `cltv.kpi.${kSlug}.subtitle`, card.subtitle);
              const kBadge = resolveKpiLabel(t, `cltv.kpi.${kSlug}.badge`, card.badge);
              const kTooltip = getCltvKpiDefinition(kSlug, t);
              const tooltipAlign = index >= 2 ? "end" : "start";

              const kChangeFormatted = isChurnKpiCard(card)
                ? undefined
                : card.percentageChangeFormattedValue?.trim() || undefined;
              return (
                <DashboardKpiCard
                  key={card.id}
                  className="cltv-dashboard__kpi-card"
                  uppercaseTitle={false}
                  title={
                    <span className="cltv-dashboard__kpi-title-row">
                      <span className="cltv-dashboard__kpi-title-text">{kTitle}</span>
                      {kBadge ? <span className="cltv-dashboard__badge">{kBadge}</span> : null}
                    </span>
                  }
                  metricLabelForTooltip={kTitle}
                  value={normalizeCurrencyLabel(card.formattedValue ?? card.value)}
                  sublabel={kSubtitle}
                  tooltip={kTooltip}
                  tooltipAlign={tooltipAlign}
                  changePercent={(() => {
                    const raw = Number(card.percentageChange);
                    if (!Number.isFinite(raw)) {
                      return undefined;
                    }
                    return isChurnKpiCard(card) ? -raw : raw;
                  })()}
                  changePercentFormattedValue={kChangeFormatted}
                  changePercentTreatZeroAsMissing
                  changePercentClampAbsMax={DASHBOARD_CHANGE_FALLBACK_MAX}
                  changePercentFallbackSeed={`cltv-kpi-${card.id}`}
                  comparisonLabel={t("cltv.kpi.vsLastPeriod")}
                  formulaTitle={t("cltv.common.formula")}
                  hideNoTrendFallback
                  icon={getKpiIcon(card)}
                />
              );
            })
          )}
        </section>

        <section className="cltv-dashboard__dual">
          <article className="cltv-dashboard__panel cltv-dashboard__panel--segment-distribution">
            <CltvSectionTitle
              title={t("cltv.sections.segmentDistribution")}
              tooltip={getCltvSectionDefinition("segmentDistribution", t)}
            />
            {kpiLoading ? (
              <LoadingSpinner compact />
            ) : pieData.length === 0 ? (
              <p className="cltv-dashboard__empty">{t("cltv.empty.noSegment")}</p>
            ) : (
              <div className="cltv-dashboard__segment-chart-wrap">
                {(() => {
                  const max = pieData.reduce((m, r) => Math.max(m, r.value), 0);
                  const min = pieData.filter(r => r.value > 0).reduce((m, r) => Math.min(m, r.value), max);
                  return (
                    <div className="cltv-dashboard__hbar-chart">
                      {pieData.map(item => (
                        <div key={item.segmentKey} className="cltv-dashboard__hbar-row">
                          <span className="cltv-dashboard__hbar-label">{item.name}</span>
                          <div className="cltv-dashboard__hbar-track">
                            <div
                              className="cltv-dashboard__hbar-fill"
                              style={{
                                width: max > 0 ? `${calculateBarWidthPct(item.value, max, min)}%` : "0%",
                                backgroundColor: item.color,
                              }}
                            />
                          </div>
                          <span className="cltv-dashboard__hbar-value">
                            {item.countFormattedValue ?? formatCount(item.value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </article>

          <article className="cltv-dashboard__panel">
            <CltvSectionTitle
              title={t("cltv.sections.predictiveInsights")}
              tooltip={getCltvSectionDefinition("predictiveInsights", t)}
            />
            {kpiLoading ? (
              <LoadingSpinner compact />
            ) : (
              <div className="cltv-dashboard__insights">
                {(() => {
                  const revenueItem = predictiveInsights.find(i => i.id === "revenue-growth");
                  const riskTierItem = predictiveInsights.find(i => i.id === "risk-tier-analytics");
                  const churnItem = predictiveInsights.find(i => i.id === "churn-alert" || i.id === "churn-risk-alert");

                  return (
                    <>
                      {revenueItem && (
                        <div className="cltv-dashboard__pi-revenue">
                          <p className="cltv-dashboard__pi-revenue-label">
                            {revenueItem.title}
                          </p>
                          <h3 className="cltv-dashboard__pi-revenue-value">
                            {normalizeCurrencyLabel(revenueItem.formattedValue ?? revenueItem.value)}
                          </h3>
                          <p className="cltv-dashboard__pi-revenue-desc">{revenueItem.description}</p>
                        </div>
                      )}

                      {riskTierItem && riskTierItem.tiers && riskTierItem.tiers.length > 0 && (
                        <div className="cltv-dashboard__pi-risk">
                          <h4 className="cltv-dashboard__pi-risk-title">{riskTierItem.title}</h4>
                          <div className="cltv-dashboard__pi-tiers">
                            {riskTierItem.tiers
                              .map((tier: RiskTier, tierIndex) => ({
                                tier,
                                tierIndex,
                                code: cltvRiskTierCode(tier.tier, t),
                              }))
                              // Ordered high → medium → low. Bands the API labels in a way this
                              // app can't map keep their original position rather than jumping
                              // to the end of the list.
                              .sort(
                                (a, b) =>
                                  (a.code ? RISK_TIER_ORDER[a.code] : a.tierIndex) -
                                  (b.code ? RISK_TIER_ORDER[b.code] : b.tierIndex)
                              )
                              .map(({ tier, code }) => (
                              <div key={tier.tier} className="cltv-dashboard__pi-tier">
                                <div className="cltv-dashboard__pi-tier-head">
                                  <span className="cltv-dashboard__pi-tier-name">{tier.tier}</span>
                                  <span className="cltv-dashboard__pi-tier-count">
                                    {tier.countFormattedValue ?? formatCount(tier.count)} (
                                    {tier.percentageFormattedValue ?? formatPercent(tier.percentage)}
                                    )
                                  </span>
                                </div>
                                <div className="cltv-dashboard__progress">
                                  <span
                                    style={{
                                      width: `${tier.percentage}%`,
                                      backgroundColor: code
                                        ? RISK_TIER_COLORS[code]
                                        : RISK_TIER_FALLBACK_COLOR,
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {churnItem && (
                        <div className="cltv-dashboard__pi-churn-alert">
                          <div className="cltv-dashboard__pi-churn-header">
                            <span className="cltv-dashboard__pi-churn-icon">
                              <AlertTriangle size={16} />
                            </span>
                            <span className="cltv-dashboard__pi-churn-label">
                              {churnItem.title}
                            </span>
                            {churnItem.actionLabel && (
                              <span className="cltv-dashboard__pi-churn-badge">
                                {churnItem.actionLabel}
                              </span>
                            )}
                          </div>
                          <p className="cltv-dashboard__pi-churn-count">
                            {churnItem.countLabel ??
                              t("cltv.insights.customersAtHighRisk", {
                                value: formatCount(churnItem.count ?? 0),
                              })}
                          </p>
                          <p className="cltv-dashboard__pi-churn-desc">{churnItem.description}</p>
                        </div>
                      )}

                      {insightRows
                        .filter(r =>
                          r.key !== "revenue-growth" &&
                          r.key !== "risk-tier-analytics" &&
                          r.key !== "churn-alert" &&
                          r.key !== "churn-risk-alert"
                        )
                        .map(insight => {
                          const iTitle = insight.raw.title;
                          const iDesc = insight.raw.description;
                          const iValue = normalizeCurrencyLabel(
                            insight.raw.formattedValue ?? insight.raw.value,
                          );
                          const iBadge = insight.raw.badge;
                          const insightTooltip = getCltvInsightDefinition(insight.key, t);
                          return (
                            <div key={insight.key} className="cltv-dashboard__insight">
                              <div className="cltv-dashboard__insight-head">
                                <div className="cltv-dashboard__insight-left">
                                  <span
                                    className={`cltv-dashboard__insight-icon ${insight.tone === "warning"
                                      ? "cltv-dashboard__insight-icon--warning"
                                      : insight.tone === "positive"
                                        ? "cltv-dashboard__insight-icon--positive"
                                        : "cltv-dashboard__insight-icon--info"
                                      }`}
                                  >
                                    {insightIcon(insight.tone)}
                                  </span>
                                  <div>
                                    <div className="cltv-dashboard__insight-title-row">
                                      <h4>{iTitle}</h4>
                                      {insightTooltip ? (
                                        <DefinitionTooltip
                                          label={iTitle}
                                          info={insightTooltip}
                                          formulaTitle={t("cltv.common.formula")}
                                        />
                                      ) : null}
                                    </div>
                                    <p>{iDesc}</p>
                                  </div>
                                </div>
                                <div className="cltv-dashboard__insight-right">
                                  <strong>{iValue}</strong>
                                  {iBadge ? <span>{iBadge}</span> : null}
                                </div>
                              </div>
                              <div className="cltv-dashboard__progress">
                                <span
                                  style={{
                                    width: `${insight.progress}%`,
                                    backgroundColor: insightToneColor(insight.tone),
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </>
                  );
                })()}
              </div>
            )}
          </article>
        </section>

        <article ref={spendingPanelRef} className="cltv-dashboard__panel cltv-dashboard__panel--spending">
          <div className="cltv-dashboard__spending-header">
            <div>
              <CltvSectionTitle
                title={t("cltv.sections.spendingPatterns")}
                tooltip={getCltvSectionDefinition("spendingPatterns", t)}
              />
            </div>
            <div className="cltv-dashboard__spending-legend">
              <span>
                <i className="is-predicted" /> {t("cltv.legend.predictedRevenue")}
              </span>
              <span>
                <i className="is-actual" /> {t("cltv.legend.actualRevenue")}
              </span>
            </div>
            {/* Own full-width rows of the header flex, so they span the panel
                instead of being squeezed into the column beside the legend. */}
            <p className="cltv-dashboard__panel-note">
              {t("cltv.notes.predictionAccuracy")}
            </p>
            <p className="cltv-dashboard__chart-subtitle">{spendingSubtitle}</p>
          </div>
          {kpiLoading && !hasLoadedSpendingPatterns ? (
            <LoadingSpinner compact />
          ) : chartsError && spendingPatterns.length === 0 ? (
            <p className="cltv-dashboard__empty" role="alert">
              {chartsError}
            </p>
          ) : (
            <div className="cltv-dashboard__chart" ref={spendingChartContainerRef}>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart
                  data={spendingChartData}
                  margin={{ top: 28, right: 20, left: 10, bottom: 10 }}
                  onMouseMove={handleSpendingChartMouseMove}
                  onMouseLeave={handleSpendingChartMouseLeave}
                >
                  <CartesianGrid stroke="#e8eef8" strokeDasharray="3 4" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: "#8aa0bf" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, spendingYAxisDomain[1]]}
                    tickFormatter={formatKLabel}
                    tick={{ fontSize: 12, fill: "#8aa0bf" }}
                    axisLine={false}
                    tickLine={false}
                    width={52}
                  />
                  <ReferenceLine
                    x={spendingChartData[3]?.label}
                    stroke="#8aa0bf"
                    strokeDasharray="6 4"
                    strokeWidth={1.5}
                    label={{
                      value: t("cltv.charts.currentMonth"),
                      position: "top",
                      fill: "#587194",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  />
                  <Tooltip cursor={{ stroke: "#c6d3e6", strokeWidth: 1 }} content={() => null} />
                  <Line
                    type="linear"
                    dataKey="predicted"
                    name="predicted"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    strokeLinecap="round"
                    dot={false}
                    activeDot={{ r: 5, fill: "#3b82f6", stroke: "#ffffff", strokeWidth: 2 }}
                  />
                  <Line
                    type="linear"
                    dataKey="actual"
                    name="actual"
                    stroke="#101828"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    dot={{ r: 4, fill: "#101828", stroke: "#ffffff", strokeWidth: 2 }}
                    connectNulls={false}
                    activeDot={{ r: 6, fill: "#101828", stroke: "#ffffff", strokeWidth: 2 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              {spendingHover && spendingChartData[spendingHover.index] ? (
                <FloatingTooltip
                  x={spendingHover.x}
                  y={spendingHover.y}
                  contentKey={spendingHover.index}
                  isRTL={isAr}
                  rtlOpenRightOrBelow={isAr}
                  boundsRef={isAr ? spendingPanelRef : spendingChartContainerRef}
                >
                  <SpendingTooltip
                    active
                    label={spendingChartData[spendingHover.index].label}
                    payload={[{ value: 0, payload: spendingChartData[spendingHover.index] }]}
                    formatBucket={formatSpendingBucket}
                    formatMoney={formatMoney}
                  />
                </FloatingTooltip>
              ) : null}
            </div>
          )}
        </article>

        <article className="cltv-dashboard__panel cltv-dashboard__panel--evolution">
          <div className="cltv-dashboard__evolution-header">
            <CltvSectionTitle
              title={t("cltv.sections.segmentEvolution")}
              tooltip={getCltvSectionDefinition("segmentEvolution", t)}
            />
            {activeEvolutionSeries.length > 0 && (
              <div className="cltv-dashboard__line-legend">
                {activeEvolutionSeries.map(series => (
                  <span key={series.key}>
                    <i style={{ backgroundColor: series.color }} />
                    {t(series.labelKey)}
                  </span>
                ))}
              </div>
            )}
          </div>
          {kpiLoading ? (
            <LoadingSpinner compact />
          ) : chartsError && segmentEvolution.length === 0 ? (
            <p className="cltv-dashboard__empty" role="alert">
              {chartsError}
            </p>
          ) : segmentEvolution.length === 0 ? (
            <p className="cltv-dashboard__empty">{t("cltv.empty.noEvolution")}</p>
          ) : (
            <div className="cltv-dashboard__chart" ref={evolutionChartContainerRef}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={segmentEvolution}
                  margin={{ top: 16, right: 20, left: 10, bottom: 10 }}
                  onMouseMove={handleEvolutionChartMouseMove}
                  onMouseLeave={handleEvolutionChartMouseLeave}
                >
                  <CartesianGrid stroke="#e8eef8" strokeDasharray="3 4" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tickFormatter={formatMonthYear}
                    tick={{ fontSize: 12, fill: "#8aa0bf" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={formatCountAxis}
                    width={52}
                    tick={{ fontSize: 12, fill: "#8aa0bf" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip cursor={{ stroke: "#c6d3e6", strokeWidth: 1 }} content={() => null} />
                  {activeEvolutionSeries.map(series => (
                    <Line
                      key={series.key}
                      type="linear"
                      dataKey={series.key}
                      stroke={series.color}
                      strokeWidth={2}
                      strokeOpacity={0.9}
                      dot={{ r: 4, fill: "#ffffff", stroke: series.color, strokeWidth: 2 }}
                      activeDot={{ r: 5.5, fill: "#ffffff", stroke: series.color, strokeWidth: 2 }}
                      connectNulls={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              {evolutionTooltipPayload ? (
                <FloatingTooltip
                  x={evolutionHover!.x}
                  y={evolutionHover!.y}
                  contentKey={evolutionHover!.index}
                  isRTL={isAr}
                  boundsRef={evolutionChartContainerRef}
                >
                  <EvolutionTooltip
                    active
                    label={evolutionTooltipPayload.label}
                    payload={evolutionTooltipPayload.payload}
                    formatDate={formatMonthYear}
                  />
                </FloatingTooltip>
              ) : null}
            </div>
          )}
        </article>

        <article className="cltv-dashboard__panel cltv-dashboard__panel--customer-analytics">
          <div className="cltv-dashboard__customer-analytics">
            <div className="cltv-dashboard__table-toolbar">
              <div className="cltv-dashboard__table-toolbar-left">
                <div className="cltv-dashboard__title-with-tooltip cltv-dashboard__title-with-tooltip--table">
                  <h3 className="cltv-dashboard__data-table-title">
                    {t("cltv.sections.customerTable")}
                  </h3>
                  {customerTableTooltip ? (
                    <DefinitionTooltip
                      label={t("cltv.sections.customerTable")}
                      info={customerTableTooltip}
                      formulaTitle={t("cltv.common.formula")}
                    />
                  ) : null}
                </div>
              </div>
              <div className="cltv-dashboard__table-controls">
                <label className="cltv-dashboard__table-search">
                  <Search size={15} />
                  <input
                    type="text"
                    value={searchInput}
                    placeholder={t("cltv.table.searchPlaceholder")}
                    onChange={event => setSearchInput(event.target.value)}
                  />
                  {searchInput && (
                    <button
                      type="button"
                      className="cltv-dashboard__table-search-clear"
                      aria-label={t("cltv.table.clearSearch")}
                      onClick={() => setSearchInput("")}
                    >
                      <X size={14} />
                    </button>
                  )}
                </label>
                <StyledDropdown
                  mode="multiple"
                  value={selectedSegments}
                  placeholder={t("cltv.allSegments", { defaultValue: "All Segments" })}
                  allowClear
                  showSearch={false}
                  className="filter-dropdown"
                  popupClassName="filter-dropdown-menu"
                  style={{ minWidth: 180 }}
                  onChange={(value: string[]) => setSelectedSegments(value ?? [])}
                  options={SEGMENT_PILL_FILTERS.map(pill => ({
                    value: pill.key,
                    label: t(pill.labelKey, { defaultValue: pill.key }),
                  }))}
                />
                <button
                  type="button"
                  className={`cltv-dashboard__export-button ${
                    isExporting ? "cltv-dashboard__export-button--loading" : ""
                  }`.trim()}
                  onClick={handleExportCustomerCsv}
                  disabled={sortedCustomerRows.length === 0 || isExporting}
                >
                  <Download size={16} />
                  {isExporting ? t("cltv.table.exporting") : t("cltv.table.export")}
                  {isExporting ? (
                    <CircularLoader
                      size={16}
                      thickness={2}
                      className="cltv-dashboard__export-loader"
                    />
                  ) : null}
                </button>
              </div>
            </div>

            {customerError ? (
              <p className="cltv-dashboard__error-banner" role="alert">
                {customerError}
              </p>
            ) : null}

            <DashboardDataTableScroll className="cltv-dashboard__table-wrap">
            {customerLoading ? (
              <LoadingSpinner compact />
            ) : (
              <DashboardDataTableTable className="cltv-dashboard__customer-table">
                <thead>
                  <tr>
                    <th>
                      <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "customerName"))}>
                        {t("cltv.headers.customerName")}
                      </DashboardDataTableSortButton>
                    </th>
                    <th>
                      <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "email"))}>
                        {t("cltv.headers.customerEmail")}
                      </DashboardDataTableSortButton>
                    </th>
                    <th>
                      <span className={dt.thHelpRow}>
                        <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "profile"))}>
                          {t("cltv.headers.customerProfile")}
                        </DashboardDataTableSortButton>
                        {columnTooltips.profile ? (
                          <DefinitionTooltip
                            label={t("cltv.headers.customerProfile")}
                            info={columnTooltips.profile}
                            formulaTitle={t("cltv.common.formula")}
                          />
                        ) : null}
                      </span>
                    </th>
                    <th className={dt.number}>
                      <span className={dt.thHelpRow}>
                        <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "totalSpend"))}>
                          {t("cltv.headers.totalSpend")}
                        </DashboardDataTableSortButton>
                        {columnTooltips.totalSpend ? (
                          <DefinitionTooltip
                            label={t("cltv.headers.totalSpend")}
                            info={columnTooltips.totalSpend}
                            formulaTitle={t("cltv.common.formula")}
                          />
                        ) : null}
                      </span>
                    </th>
                    <th className={dt.number}>
                      <span className={dt.thHelpRow}>
                        <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "totalOrders"))}>
                          {t("cltv.headers.totalOrders")}
                        </DashboardDataTableSortButton>
                        {columnTooltips.totalOrders ? (
                          <DefinitionTooltip
                            label={t("cltv.headers.totalOrders")}
                            info={columnTooltips.totalOrders}
                            formulaTitle={t("cltv.common.formula")}
                          />
                        ) : null}
                      </span>
                    </th>
                    <th className={dt.number}>
                      <span className={dt.thHelpRow}>
                        <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "predictedSpend"))}>
                          {t("cltv.headers.predictedSpend3m")}
                        </DashboardDataTableSortButton>
                        {columnTooltips.predictedSpend ? (
                          <DefinitionTooltip
                            label={t("cltv.headers.predictedSpend3m")}
                            info={columnTooltips.predictedSpend}
                            formulaTitle={t("cltv.common.formula")}
                            align="end"
                          />
                        ) : null}
                      </span>
                    </th>
                    <th className={dt.number}>
                      <span className={dt.thHelpRow}>
                        <DashboardDataTableSortButton onClick={() => setTableSort(current => toggleTableSort(current, "predictedOrders"))}>
                          {t("cltv.headers.predictedOrders3m")}
                        </DashboardDataTableSortButton>
                        {columnTooltips.predictedOrders ? (
                          <DefinitionTooltip
                            label={t("cltv.headers.predictedOrders3m")}
                            info={columnTooltips.predictedOrders}
                            formulaTitle={t("cltv.common.formula")}
                            align="end"
                          />
                        ) : null}
                      </span>
                    </th>
                    <th>
                      <span className={dt.thHelpRow}>
                        {t("cltv.headers.segments")}
                        {columnTooltips.segments ? (
                          <DefinitionTooltip
                            label={t("cltv.headers.segments")}
                            info={columnTooltips.segments}
                            formulaTitle={t("cltv.common.formula")}
                            align="end"
                          />
                        ) : null}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCustomerRows.map(row => {
                    const totalOrders = getTotalOrders(row);
                    const totalSpend = getTotalSpend(row);
                    const predictedOrders = getPredictedOrders(row);
                    const monthsActive = Math.max(1, row.customerAgeMonths ?? totalOrders * 6);
                    const profileStrength = Math.min(100, Math.round((totalSpend / maxObservedSpend) * 100));
                    const segments = segmentTonesForRow(row);

                    return (
                      <tr key={`${row.customerId}-${row.email}`}>
                        <td className="cltv-dashboard__customer-name">
                          {row.customerName || row.customerId || <span className={dt.empty}>{emptyCell}</span>}
                        </td>
                        <td className="cltv-dashboard__email">
                          {row.email ? row.email : <span className={dt.empty}>{emptyCell}</span>}
                        </td>
                        <td>
                          <div className="cltv-dashboard__profile-cell">
                            <strong>{getCustomerLifetimeLabel(monthsActive, t, i18n.language)}</strong>
                            <span className="cltv-dashboard__profile-subtext">
                              {t("cltv.table.observedInWindows")}
                            </span>
                            <div className="cltv-dashboard__profile-meter">
                              <span style={{ width: `${profileStrength}%` }} />
                            </div>
                          </div>
                        </td>
                        <td className={`${dt.number} cltv-dashboard__numeric`}>
                          {formatMoneyCell(row.totalSpendFormattedValue, row.totalSpendSince2023, formatMoneyShort) ?? <span className={dt.empty}>{emptyCell}</span>}
                        </td>
                        <td className={`${dt.number} cltv-dashboard__numeric`}>
                          {row.totalOrdersSince2023 !== null && row.totalOrdersSince2023 !== undefined ? formatCount(totalOrders) : <span className={dt.empty}>{emptyCell}</span>}
                        </td>
                        <td className={`${dt.number} cltv-dashboard__numeric`}>
                          {formatMoneyCell(row.predictedSpend3mFormattedValue, row.predictedSpend3m, formatMoneyShort) ?? <span className={dt.empty}>{emptyCell}</span>}
                        </td>
                        <td className={`${dt.number} cltv-dashboard__numeric`}>
                          {row.predictedOrders3m !== null && row.predictedOrders3m !== undefined ? formatCount(predictedOrders) : <span className={dt.empty}>{emptyCell}</span>}
                        </td>
                        <td className="cltv-dashboard__segments-cell">
                          {segments.length > 0 ? (
                            <div className="cltv-dashboard__segment-badges">
                              {segments.slice(0, 2).map(segment => (
                                <span
                                  key={`${row.customerId}-${segment}`}
                                  className={`cltv-dashboard__segment-pill cltv-dashboard__segment-pill--${segment}`}
                                >
                                  {getCustomerSegmentLabel(segment, t)}
                                </span>
                              ))}
                              {segments.length > 2 && (
                                <span className="cltv-dashboard__segment-pill cltv-dashboard__segment-pill--overflow">
                                  +{formatCount(segments.length - 2)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className={dt.empty}>{emptyCell}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </DashboardDataTableTable>
            )}
            </DashboardDataTableScroll>

            {!customerError && !customerLoading ? (
              <div
                className="cltv-dashboard__table-pagination"
                dir={isAr ? "rtl" : "ltr"}
              >
                <TablePagePagination
                  currentPage={page}
                  totalPages={customerTableTotalPages}
                  onPrevious={() => setPage(p => Math.max(1, p - 1))}
                  onNext={() => setPage(p => Math.min(customerTableTotalPages, p + 1))}
                />
              </div>
            ) : null}
          </div>
        </article>
      </div>
    </div>
  );
};

export default CLTVDashboard;
